// Phase 1A Step 35 — SIKA conversation mock card.
// Cycles through SIKA_TURNS at 6s intervals so the section feels alive
// without requiring user input on the landing page.
// Visual language matches handoff design: surface card, accent border on
// SIKA's plan card, mono labels, Bebas Neue for numerals.
//
// SikaPlanCard is extracted to its own file to keep this orchestrator
// under the 250-LOC ceiling.

import { useEffect, useState } from "react";
import { SIKA_TURNS } from "./sika-data.ts";
import { SikaPlanCard } from "./SikaPlanCard.tsx";

export function SikaConversation(): JSX.Element {
  const [idx, setIdx] = useState<number>(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % SIKA_TURNS.length), 6000);
    return () => clearInterval(t);
  }, []);
  const turn = SIKA_TURNS[idx]!;

  return (
    <div
      className="sz-l-sika-card"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        padding: "32px 32px 28px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Conversation header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="live-dot" />
          <span
            className="mono"
            style={{
              fontSize: 10,
              letterSpacing: "0.18em",
              color: "var(--accent)",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            SIKA · The Council
          </span>
        </div>
        <span
          className="mono"
          style={{
            fontSize: 9,
            letterSpacing: "0.16em",
            color: "var(--text-dim)",
            textTransform: "uppercase",
          }}
        >
          Goal · {turn.category}
        </span>
      </div>

      {/* User bubble — left aligned, dim */}
      <div className="sz-l-sika-bubble-row" style={{ marginBottom: 18, transition: "all .4s" }}>
        <div
          className="mono"
          style={{
            fontSize: 9,
            color: "var(--text-dim)",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            marginBottom: 6,
          }}
        >
          You
        </div>
        <div
          style={{
            fontSize: 16,
            color: "var(--text)",
            lineHeight: 1.4,
            padding: "12px 16px",
            background: "rgba(255, 255, 255, 0.02)",
            border: "1px solid var(--border)",
            maxWidth: "92%",
          }}
        >
          {turn.userPrompt}
        </div>
      </div>

      {/* SIKA bubble — right aligned, accent */}
      <div
        className="sz-l-sika-bubble-row"
        style={{
          marginBottom: 24,
          textAlign: "right",
          transition: "all .4s",
        }}
      >
        <div
          className="mono"
          style={{
            fontSize: 9,
            color: "var(--accent)",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            marginBottom: 6,
            fontWeight: 600,
          }}
        >
          SIKA
        </div>
        <div
          style={{
            fontSize: 14,
            color: "var(--text)",
            lineHeight: 1.55,
            padding: "14px 18px",
            background: "rgba(212, 168, 67, 0.04)",
            border: "1px solid var(--accent-deep)",
            maxWidth: "92%",
            marginLeft: "auto",
            textAlign: "left",
          }}
        >
          {turn.sikaResponse}
        </div>
      </div>

      {/* Plan card — generated by SIKA, slid into the conversation */}
      <SikaPlanCard plan={turn.plan} />
    </div>
  );
}
